import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PidStage from '../components/PidStage';
import LiveCurvePanel from '../components/LiveCurvePanel';
import LiveControlPanel from '../components/LiveControlPanel';
import { useLive, useLiveDevices } from '../hooks';
import { api } from '../api';
import type { ControlAction } from '@ccs/live-contract';
import { formatClock, formatRows, formatTime } from '../../../shared/format';

function phaseLabel(phase: string | undefined): { text: string; className: string } {
  if (phase === 'live') return { text: '实时', className: 'ok' };
  if (phase === 'stale') return { text: '数据陈旧', className: 'warn' };
  if (phase === 'idle') return { text: '空闲', className: '' };
  return { text: '离线', className: 'danger' };
}

function modeLabel(mode: string | undefined): string {
  if (mode === 'pid') return 'PID';
  if (mode === 'freq') return '频率';
  return '停止';
}

export default function Live() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const devicesQuery = useLiveDevices();
  const devices = devicesQuery.data ?? [];
  const activeId = deviceId ?? devices[0]?.id;
  const live = useLive(activeId);

  const [selected, setSelected] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [commandBusy, setCommandBusy] = useState(false);
  const [commandError, setCommandError] = useState<string | null>(null);

  useEffect(() => {
    if (!deviceId && devices.length) {
      navigate(`/ccs/live/${devices[0].id}`, { replace: true });
    }
  }, [deviceId, devices, navigate]);

  useEffect(() => {
    setSelected(null);
    setZoom(1);
  }, [activeId]);

  const device = live.device ?? devices.find((item) => item.id === activeId) ?? null;
  const session = live.session;
  const status = live.status;
  const devicePhase = status?.phase ?? device?.phase ?? 'offline';
  const phase = phaseLabel(devicePhase);
  const connectionBadge =
    live.connection === 'open'
      ? null
      : live.connection === 'connecting'
        ? '连接中…'
        : live.connection === 'reconnecting'
          ? '连接中断，正在重连'
          : '连接已关闭';

  const lastSample = live.sample?.ts ?? null;
  const desired = status?.control?.desired ?? null;

  const events = useMemo(() => live.events.slice(0, 20), [live.events]);

  const sendCommand = async (action: ControlAction, params: Record<string, unknown>) => {
    if (!activeId) return;
    setCommandBusy(true);
    setCommandError(null);
    try {
      await api(`/api/ccs/live/devices/${encodeURIComponent(activeId)}/commands`, {
        method: 'POST',
        body: JSON.stringify({ action, params }),
      });
    } catch (error) {
      setCommandError(error instanceof Error ? error.message : '控制命令发送失败');
    } finally {
      setCommandBusy(false);
    }
  };

  if (devicesQuery.isLoading) {
    return <div className="empty">加载实时设备…</div>;
  }

  if (devicesQuery.isError) {
    return (
      <div className="empty">
        <b>无法读取实时设备</b>
        请确认已登录，并且服务端已启用实时模块。
      </div>
    );
  }

  if (!devices.length) {
    return (
      <div className="empty">
        <b>还没有注册实时设备</b>
        <p style={{ marginTop: 8 }}>
          请管理员在「设备」页创建一台实时设备，复制一次性令牌写入工控机桥接配置。
          本地无硬件联调也可以用 Mock Bridge。
        </p>
        <pre className="live-code">
{`npm run -w @ccs/api device -- create ccs-lab-01 "CCS 实验台"
CCS_DEVICE_TOKEN=<token> npm run mock:bridge`}
        </pre>
      </div>
    );
  }

  return (
    <div className="live">
      <div className="replay-head">
        <div>
          <Link to="/ccs" className="muted" style={{ fontSize: 12 }}>
            ← 返回会话
          </Link>
          <div className="replay-title">
            <div className="eyebrow" style={{ marginTop: 8 }}>
              Live · 实时
            </div>
            <h1>{device?.name ?? activeId}</h1>
            <div className="replay-meta">
              <span className="mono">{session?.sessionId ?? '等待采集会话'}</span>
              {session ? <span>开始 {formatClock(session.startedAt)}</span> : null}
              {session ? <span>{formatRows(session.rowCount)} 行</span> : null}
              <span>1 Hz</span>
              {lastSample ? <span>最近样本 {formatTime(lastSample)}</span> : null}
            </div>
          </div>
        </div>
        <div className="badge-row">
          <span className={`badge ${phase.className}`}>{phase.text}</span>
          {status?.acq ? <span className="badge">采集 {status.acq}</span> : null}
          {status?.stm32 ? <span className="badge">串口 {status.stm32}</span> : null}
          {device?.bridgeVersion ? <span className="badge">{device.bridgeVersion}</span> : null}
          {session ? <span className="badge accent">实时数据集</span> : null}
          {devices.length > 1 ? (
            <select
              className="input live-select"
              value={activeId}
              onChange={(event) => navigate(`/ccs/live/${event.target.value}`)}
            >
              {devices.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </div>

      {connectionBadge ? (
        <div className="live-banner">
          <span className="live-banner-dot" aria-hidden />
          {connectionBadge}。页面保留最后一帧，恢复后会自动对齐。
        </div>
      ) : null}
      {status && status.phase !== 'live' && session ? (
        <div className="live-banner warn">
          实时数据已{status.phase === 'stale' ? '陈旧' : '离线'}；最后心跳{' '}
          {device?.lastSeenAt ? formatClock(device.lastSeenAt) : '未知'}。控制类操作在设计上会被禁用。
        </div>
      ) : null}
      {status?.stm32 && status.stm32 !== 'ok' ? (
        <div className="live-banner warn">
          STM32 串口状态：{status.stm32 === 'shelved' ? '已搁置' : '未连接'}。设备控制暂不可用；
          请在工控机本地状态页 `http://127.0.0.1:9780/` 检查接线后点「重新扫描串口」。
          {status.stm32 === 'lost' ? ' 桥接会自动重连。' : ' 桥接会定时重试。'}
        </div>
      ) : null}

      <section className="panel pid-panel">
        <div className="panel-toolbar">
          <div className="zoom-group">
            <button
              type="button"
              onClick={() => setZoom((value) => Math.max(0.5, value - 0.25))}
              title="缩小"
            >
              −
            </button>
            <span>{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              onClick={() => setZoom((value) => Math.min(4, value + 0.25))}
              title="放大"
            >
              ＋
            </button>
            <button type="button" className="wide" onClick={() => setZoom(1)} title="适应宽度">
              适应
            </button>
          </div>
          <span className="badge" style={{ background: '#F0B0B1', color: '#0b1220' }}>
            温度
          </span>
          <span className="badge" style={{ background: '#B0DDC1', color: '#0b1220' }}>
            流量 / 液位
          </span>
          <span className="badge" style={{ background: '#AAD3F5', color: '#0b1220' }}>
            压力
          </span>
          <div className="spacer" />
          {session ? (
            <Link className="btn btn-ghost btn-sm" to={`/ccs/datasets/${session.datasetId}`}>
              打开回放
            </Link>
          ) : null}
          <span className="muted" style={{ fontSize: 11 }}>
            点击图上数值查看 5 分钟实时曲线
          </span>
        </div>
        <PidStage
          sample={live.sample}
          zoom={zoom}
          selected={selected}
          onSelect={(key) => setSelected((current) => (current === key ? null : key))}
        />
      </section>

      {selected ? (
        <LiveCurvePanel
          samples={live.samples}
          channelKey={selected}
          onClose={() => setSelected(null)}
        />
      ) : null}

      <div className="live-grid">
        <section className="panel live-status-card">
          <div className="panel-head">
            <div>
              <div className="eyebrow">Bridge</div>
              <h2>链路与数据质量</h2>
            </div>
            <span className={`badge ${phase.className}`}>{phase.text}</span>
          </div>
          {status ? (
            <div className="live-metrics">
              <div className="metric">
                <b>{status.rowRateHz.toFixed(2)}</b>
                <span>行 / 秒</span>
              </div>
              <div className="metric">
                <b>{formatRows(session?.rowCount ?? 0)}</b>
                <span>本会话行数</span>
              </div>
              <div className="metric">
                <b>{status.dropped}</b>
                <span>队列丢弃</span>
              </div>
              <div className="metric">
                <b>{status.syncLoss}</b>
                <span>失步丢秒</span>
              </div>
              <div className="metric">
                <b>{status.recording ? `${status.recording.diskFreeGb.toFixed(1)} GB` : '—'}</b>
                <span>本地磁盘剩余</span>
              </div>
              <div className="metric">
                <b className={status.clockSkewMs !== null && Math.abs(status.clockSkewMs) > 2000 ? 'is-warn' : undefined}>
                  {status.clockSkewMs === null ? '—' : `${status.clockSkewMs} ms`}
                </b>
                <span>时钟偏差</span>
              </div>
            </div>
          ) : (
            <div className="empty">等待桥接状态报文…</div>
          )}
          <div className="live-status-lines">
            <div>
              <span>采集</span>
              <b>{status?.acq ?? '—'}</b>
            </div>
            <div>
              <span>STM32</span>
              <b>{status?.stm32 ?? '—'}</b>
            </div>
            <div>
              <span>会话</span>
              <b className="mono">{session?.sessionId ?? '—'}</b>
            </div>
            <div>
              <span>最近心跳</span>
              <b>{device?.lastSeenAt ? formatClock(device.lastSeenAt) : '—'}</b>
            </div>
          </div>
        </section>

        <section className="panel live-events">
          <div className="panel-head">
            <div>
              <div className="eyebrow">Events</div>
              <h2>事件</h2>
            </div>
            <span className="badge">{events.length}</span>
          </div>
          {events.length ? (
            <div className="live-event-list">
              {events.map((event, index) => (
                <div className="live-event" key={`${event.ts}-${event.code}-${index}`}>
                  <span className={`live-event-level ${event.level}`}>{event.level}</span>
                  <div>
                    <b>{event.code}</b>
                    <div className="live-event-message">{event.message}</div>
                    <span className="muted">{formatClock(event.ts)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty">暂无事件</div>
          )}
        </section>
      </div>

      <LiveControlPanel
        phase={devicePhase}
        status={status}
        sample={live.sample}
        canControl={live.canControl}
        commands={live.commands}
        busy={commandBusy}
        error={commandError}
        onCommand={sendCommand}
      />
    </div>
  );
}